import { createOfflineQueue, type OfflineQueueStore } from '@cuito/api-client';
import type { SyncOperationInput } from '@cuito/types';

/**
 * 离线队列的**本地库**（`packages/api-client/src/offlineQueue.ts`，R27 的冻结接口）。
 *
 * ## 为什么这里只做一个「工厂 + 订阅」，不重新实现队列
 *
 * 队列的读写（IndexedDB 库名 `cuito-tenant`、仓名 `pending-orders`、逐条发送不并发）已经由
 * `@cuito/api-client` 交付并有自己的单测（`__tests__/offlineQueue.spec.ts` 的 8 条）。
 * 收银端这一侧需要的只是三件它没有的事：
 *   ① **浏览器接缝**：`indexedDB` 只在真浏览器里有（jsdom 没有）。缺了它就返回 `null`，
 *      **不伪造一个内存队列** —— 内存队列在测试里绿、在断网的平板上丢掉全部待上传记录，
 *      那种「测试环境比生产更宽容」的接缝正是本仓最恨的安静失败；
 *   ② **写入口的唯一处**（`enqueueOperation`）：入队后通知订阅者，状态条才会实时更新；
 *   ③ **载荷的读出校验**（`asSyncOperation`）：队列里躺着的是 `unknown`，
 *      一条读不出来的记录必须被当成失败留在队列里，而不是让页面白屏。
 */

/** 浏览器里的队列库；没有 `indexedDB`（jsdom / SSR / 老浏览器）⇒ `null` */
export function browserOfflineQueue(): OfflineQueueStore | null {
  const factory = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  return factory === undefined ? null : createOfflineQueue(factory);
}

/**
 * 队列内容的**变化通知**（`05-ui-design-system.md` §6.8 实现要点 2：
 * 「待上传数量变化时状态条文案实时更新（订阅 store，**不用轮询**）」）。
 *
 * IndexedDB 没有变更事件，而本应用里**所有**队列写入都经过 `enqueueOperation` /
 * `syncPendingOrders`（见 `useOfflineQueue`）⇒ 一个进程内的修订号就是完整的事实。
 * 用修订号而不是把条目放进模块状态：条目是异步读出来的（`getPendingOrders`），
 * 复制一份进模块变量就等于给队列造了第二份会漂移的副本。
 */
const listeners = new Set<() => void>();
let revision = 0;

export function subscribeOfflineQueue(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `useSyncExternalStore` 的快照：一个单调递增的整数（相等即「没变」） */
export function offlineQueueRevision(): number {
  return revision;
}

/** 队列可能变了（入队 / 同步 / 清条）——通知所有订阅者 */
export function notifyOfflineQueueChanged(): void {
  revision += 1;
  for (const listener of [...listeners]) listener();
}

/**
 * 入队（离线接单 / 挑拣 / 收款 / 交付 / 取消的**唯一**写入口）。
 *
 * `store === null` ⇒ 抛错，由调用方如实告诉店员「本机没有离线队列」。
 * 静默丢弃是最坏的一种：店员以为单子记下了，而 IndexedDB 里什么都没有。
 */
export async function enqueueOperation(
  store: OfflineQueueStore | null,
  operation: SyncOperationInput,
): Promise<void> {
  if (store === null) throw new Error('OFFLINE_QUEUE_UNAVAILABLE');
  await store.queueOrder(operation);
  notifyOfflineQueueChanged();
}

/** 队列里一条记录的读出形状（`payload` 是 `unknown`，读之前必须过这道校验） */
export function asSyncOperation(payload: unknown): SyncOperationInput | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const candidate = payload as Partial<SyncOperationInput>;
  if (typeof candidate.opId !== 'string' || candidate.opId === '') return null;
  if (typeof candidate.type !== 'string') return null;
  if (typeof candidate.orderNo !== 'string') return null;
  if (typeof candidate.occurredAt !== 'string') return null;
  return candidate as SyncOperationInput;
}
